// Compute the grade label and colour for a numeric test result given a
// baremo (bands) and a direction ("lower" = lowest value is best).
//
//   baremo = { excelente: number, notable: number, bien: number }
//
// Returns { label, color, bg } – tailwind classes ready to use.
export function gradeResult(value, baremo, direction = "lower") {
  if (value == null || value === "" || isNaN(value) || !baremo) {
    return { label: "—", color: "text-slate-400", bg: "bg-slate-50" };
  }
  const v = Number(value);
  const cmp = direction === "lower"
    ? (a, b) => a <= b
    : (a, b) => a >= b;

  if (baremo.excelente != null && cmp(v, baremo.excelente)) {
    return { label: "Excelente", color: "text-emerald-700", bg: "bg-emerald-100" };
  }
  if (baremo.notable != null && cmp(v, baremo.notable)) {
    return { label: "Notable", color: "text-blue-700", bg: "bg-blue-100" };
  }
  if (baremo.bien != null && cmp(v, baremo.bien)) {
    return { label: "Bien", color: "text-amber-700", bg: "bg-amber-100" };
  }
  return { label: "Mejorable", color: "text-rose-700", bg: "bg-rose-100" };
}

// Tailwind classes for a stored grade label (used when we already have the
// grade that was computed and saved at measurement time).
export const GRADE_STYLES = {
  Excelente: { color: "text-emerald-700", bg: "bg-emerald-100" },
  Notable: { color: "text-blue-700", bg: "bg-blue-100" },
  Bien: { color: "text-amber-700", bg: "bg-amber-100" },
  Mejorable: { color: "text-rose-700", bg: "bg-rose-100" },
};

export function gradeStyle(label) {
  const s = GRADE_STYLES[label];
  return s ? { label, ...s } : { label: "—", color: "text-slate-400", bg: "bg-slate-50" };
}

// Grade for a stored record+test: prefer the snapshot saved with the record
// (record.grades), then the protocol baremo, finally the current baremo.
export function recordGrade(record, testId, fallbackBaremo, direction) {
  const saved = record?.grades?.[testId];
  if (saved) return gradeStyle(saved);
  const v = record?.results?.[testId];
  const proto = (record?.protocol || []).find((p) => p.id === testId);
  const baremo = (proto && proto.baremo) || fallbackBaremo;
  return gradeResult(v, baremo, direction);
}

// Tests to display for a record: the active category tests plus any test that
// has data in the record but is no longer active (resolved from its protocol
// snapshot, flagged `inactive`).
export function testsForRecord(record, categoryTests) {
  const out = [...(categoryTests || [])];
  const known = new Set(out.map((t) => t.id));
  const proto = record?.protocol || [];
  Object.keys(record?.results || {}).forEach((tid) => {
    if (known.has(tid)) return;
    const p = proto.find((x) => x.id === tid) || {};
    out.push({
      id: tid,
      label: p.name || tid,
      unit: p.unit || "",
      direction: p.direction || "lower",
      attempts: p.attempts || 1,
      aggregation: p.aggregation || "best",
      inactive: true,
    });
  });
  return out;
}

export const EVALUATION_LABELS = {
  1: "Septiembre",
  2: "Enero",
  3: "Mayo",
};

// Measurement units available for configurable tests.
export const UNITS = [
  { value: "s", label: "Segundos", short: "s" },
  { value: "cm", label: "Centímetros", short: "cm" },
  { value: "m", label: "Metros", short: "m" },
  { value: "pts", label: "Puntos", short: "pts" },
  { value: "reps", label: "Repeticiones", short: "reps" },
];

export const UNIT_SHORT = UNITS.reduce((m, u) => ({ ...m, [u.value]: u.short }), {});

export const AGGREGATIONS = [
  { value: "best", label: "Mejor intento" },
  { value: "avg", label: "Media de intentos" },
  { value: "sum", label: "Suma de intentos" },
];

export const AGG_LABEL = AGGREGATIONS.reduce((m, a) => ({ ...m, [a.value]: a.label }), {});

export const CATEGORY_KEYS = [
  "prebenjamin", "benjamin", "alevin", "infantil", "cadete", "juvenil", "aficionado",
];

export const CATEGORY_LABELS = {
  prebenjamin: "Prebenjamín", benjamin: "Benjamín", alevin: "Alevín",
  infantil: "Infantil", cadete: "Cadete", juvenil: "Juvenil", aficionado: "Aficionado",
};

// Combine raw attempts into the value used for classification. Mirrors the
// backend logic so the UI can preview the grade live.
export function aggregateAttempts(attempts, method, direction = "lower") {
  const vals = (attempts || [])
    .filter((a) => a !== null && a !== "" && a !== undefined && !isNaN(a))
    .map(Number);
  if (!vals.length) return null;
  if (method === "sum") return Math.round(vals.reduce((s, v) => s + v, 0) * 1000) / 1000;
  if (method === "avg") return Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 1000) / 1000;
  return direction === "lower" ? Math.min(...vals) : Math.max(...vals);
}

export function currentSeason(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed
  // Spanish football season runs July → June. From January→June we're in the
  // "previous" season (started last summer).
  if (m >= 6) return `${y}-${String(y + 1).slice(2)}`;
  return `${y - 1}-${String(y).slice(2)}`;
}
